import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { invoicesService } from '@/services/invoices'
import type { InvoiceFormData, InvoiceFilters, CreateInvoiceData, ScheduledReminderKind } from '@/types/invoice'

export function useInvoices(filters?: InvoiceFilters) {
  const queryClient = useQueryClient()

  const invoicesQuery = useQuery({
    queryKey: ['invoices', filters],
    queryFn: () => invoicesService.getAll(filters),
  })

  const createMutation = useMutation({
    mutationFn: (data: CreateInvoiceData) => invoicesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoices-stats'] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<InvoiceFormData> }) =>
      invoicesService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoices-stats'] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => invoicesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoices-stats'] })
    },
  })

  return {
    invoices: invoicesQuery.data?.invoices ?? [],
    total: invoicesQuery.data?.total ?? 0,
    isLoading: invoicesQuery.isLoading,
    error: invoicesQuery.error,
    createInvoice: createMutation.mutateAsync,
    updateInvoice: updateMutation.mutateAsync,
    deleteInvoice: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}

export function useInvoiceById(id: string) {
  return useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesService.getById(id),
    enabled: !!id,
  })
}

export function useInvoicesStats() {
  return useQuery({
    queryKey: ['invoices-stats'],
    queryFn: () => invoicesService.getStats(),
  })
}

export function useUpdateInvoiceStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, statut, date_paiement }: { id: string; statut: string; date_paiement?: string }) =>
      invoicesService.updateStatus(id, statut, date_paiement),
    onSuccess: (_, { id, statut }) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoices-stats'] })
      // The DB trigger cancels pending reminders when statut → 'payee',
      // so refresh the badge/list immediately.
      if (statut === 'payee') {
        queryClient.invalidateQueries({ queryKey: ['scheduled-invoice-reminders', id] })
        queryClient.invalidateQueries({ queryKey: ['scheduled-invoice-reminders-pending-counts'] })
      }
    },
  })
}

export function useSendReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ invoiceId, userId }: { invoiceId: string; userId: string }) =>
      invoicesService.sendReminder(invoiceId, userId),
    onSuccess: (_, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
    },
  })
}

export function useSendPaymentThankYou() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ invoiceId, userId }: { invoiceId: string; userId: string }) =>
      invoicesService.sendPaymentThankYou(invoiceId, userId),
    onSuccess: (_, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
    },
  })
}

export function useSchedulePaymentThankYou() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      invoiceId,
      scheduledAt,
      userId,
    }: {
      invoiceId: string
      scheduledAt: Date
      userId: string
    }) => invoicesService.schedulePaymentThankYou(invoiceId, scheduledAt, userId),
    onSuccess: (_, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['scheduled-invoice-reminders'] })
      queryClient.invalidateQueries({ queryKey: ['scheduled-invoice-reminders-pending-counts'] })
    },
  })
}

export function useScheduleReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      invoiceId,
      scheduledAt,
      userId,
    }: {
      invoiceId: string
      scheduledAt: Date
      userId: string
    }) => invoicesService.scheduleReminder(invoiceId, scheduledAt, userId),
    onSuccess: (_, { invoiceId }) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['scheduled-invoice-reminders'] })
      queryClient.invalidateQueries({ queryKey: ['scheduled-invoice-reminders-pending-counts'] })
    },
  })
}

export function useScheduledReminders(invoiceId: string | undefined, kind?: ScheduledReminderKind) {
  return useQuery({
    queryKey: ['scheduled-invoice-reminders', invoiceId, kind ?? 'all'],
    queryFn: () => invoicesService.listScheduledReminders(invoiceId!, kind),
    enabled: !!invoiceId,
    refetchInterval: 30_000,
  })
}

export function usePendingRemindersByInvoiceIds(invoiceIds: string[], kind?: ScheduledReminderKind) {
  const sortedKey = [...invoiceIds].sort()
  return useQuery({
    queryKey: ['scheduled-invoice-reminders-pending-counts', kind ?? 'all', sortedKey],
    queryFn: () => invoicesService.listPendingByInvoiceIds(sortedKey, kind),
    enabled: sortedKey.length > 0,
    refetchInterval: 60_000,
  })
}

export function useCancelScheduledReminder() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => invoicesService.cancelScheduledReminder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduled-invoice-reminders'] })
      queryClient.invalidateQueries({ queryKey: ['scheduled-invoice-reminders-pending-counts'] })
    },
  })
}

export function useSendInvoice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (invoiceId: string) => invoicesService.sendInvoice(invoiceId),
    onSuccess: (_, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      queryClient.invalidateQueries({ queryKey: ['invoices'] })
      queryClient.invalidateQueries({ queryKey: ['invoices-stats'] })
    },
  })
}
